import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { object, string, number, mixed, date } from 'yup';

import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, {
  Arquivo,
  Botao,
  CaixaDeTexto,
  Campo,
  Selecao,
} from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import Usuario from 'Interface/Usuarios';
import {
  useAdicionarUsuarioMutation,
  useRecuperarUsuariosQuery,
} from 'Service';
import {
  ESCOLARIDADES,
  ESTADOS,
  ESTADOS_CIVIS,
  IMAGENS,
  MASCARA_CPF,
  MASCARA_TELEFONE,
  PERMISSOES,
  PERMISSOES_OS,
  SEXOS,
  SIM_OU_NAO,
} from 'View/constants';
import { useRecuperarCepQuery } from 'View/Service/viacep';

const regras = object({
  nome: string().required('Obrigatório'),
  apelido: string().nullable().optional(),
  email: string().email('Inválido').required('Obrigatório'),
  email_corporativo: string().email('Inválido').nullable().optional(),
  senha: string().min(6, 'Mínimo de 6 caracteres').required('Obrigatório'),
  cpf: string().required('Obrigatório'),
  pis: string().nullable().optional(),
  reservista: string().nullable().optional(),
  rg: string().nullable().optional(),
  expedicao_do_rg: date().nullable().optional(),
  orgao_emissor: string().nullable().optional(),
  estado_orgao_emissor: string().nullable().optional(),
  ctps: string().nullable().optional(),
  serie_da_ctps: string().nullable().optional(),
  emissao_da_ctps: string().nullable().optional(),
  titulo_de_eleitor: string().nullable().optional(),
  zona: string().nullable().optional(),
  secao: string().nullable().optional(),
  cnh: string().nullable().optional(),
  codigo_da_cnh: string().nullable().optional(),
  validade_da_cnh: string().nullable().optional(),
  telefone1: string().required('Obrigatório'),
  telefone2: string().nullable().optional(),
  nascimento: date().nullable().optional(),
  nacionalidade: string().nullable().optional(),
  sexo: number().required('Obrigatório'),
  cep: string().nullable().optional(),
  estado: string().nullable().optional(),
  cidade: string().nullable().optional(),
  bairro: string().nullable().optional(),
  endereco: string().nullable().optional(),
  numero: number().integer().positive('Inválido').optional(),
  complemento: string().nullable().optional(),
  nome_da_mae: string().nullable().optional(),
  nome_do_pai: string().nullable().optional(),
  escolaridade: number().nullable().optional(),
  deficiente: number().nullable().optional(),
  deficiencia: string().nullable().optional(),
  estado_civil: number().nullable().optional(),
  filhos: number().nullable().optional(),
  quantos_filhos: number().nullable().optional(),
  observacoes: string().nullable().optional(),
  indicado_por_id: number().nullable().optional(),
  imagem: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return IMAGENS.includes(valor.type);
      }
      return true;
    })
    .optional(),
  permissao: string().required('Obrigatório'),
  permissao_os: string().nullable().optional(),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const [cep, setCep] = useState<string>();
  const [adicionarUsuario] = useAdicionarUsuarioMutation();

  const { data: { usuarios } = {} } = useRecuperarUsuariosQuery({
    finder: 'listaJson',
  });

  const { data: viacep } = useRecuperarCepQuery(cep as string, {
    skip: !cep,
  });

  const enviar = async (dados: Usuario): Promise<void> => {
    try {
      await adicionarUsuario(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao('/usuarios');
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Adicionando funcionário"
        descricao="Preencha o formulário e clique em enviar para adicionar o funcionário"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row">
          <div className="col-md-9">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-8">
                  <Campo name="nome" rotulo="Nome" />
                </div>
                <div className="col-md-4">
                  <Campo name="apelido" rotulo="Apelido" />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="email"
                    rotulo="E-mail"
                    prefixar={<Icone nome="envelope-fill" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="senha"
                    rotulo="Senha"
                    type="password"
                    prefixar={<Icone nome="lock-fill" />}
                  />
                </div>
                <div className="col-md-12">
                  <Campo
                    name="email_corporativo"
                    rotulo="E-mail corporativo"
                    prefixar={<Icone nome="envelope-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="nacionalidade" rotulo="Nacionalidade" />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="nascimento"
                    type="date"
                    rotulo="Nascimento"
                    prefixar={<Icone nome="calendar-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="sexo"
                    rotulo="Sexo"
                    prefixar={<Icone nome="gender-ambiguous" />}
                    opcoes={SEXOS}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="cpf" rotulo="CPF" mascara={MASCARA_CPF} />
                </div>
                <div className="col-md-4">
                  <Campo name="pis" rotulo="PIS" />
                </div>
                <div className="col-md-4">
                  <Campo name="reservista" rotulo="Reservista" />
                </div>
                <div className="col-md-3">
                  <Campo name="rg" rotulo="RG" />
                </div>
                <div className="col-md-3">
                  <Campo
                    type="date"
                    name="expedicao_do_rg"
                    rotulo="Data de expedição do RG"
                  />
                </div>
                <div className="col-md-3">
                  <Campo name="orgao_emissor" rotulo="Orgão emissor do RG" />
                </div>
                <div className="col-md-3">
                  <Campo
                    name="estado_orgao_emissor"
                    rotulo="Estado do orgão emissor"
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="ctps" rotulo="CTPS" />
                </div>
                <div className="col-md-4">
                  <Campo name="serie_da_ctps" rotulo="N. série da CTPS" />
                </div>
                <div className="col-md-4">
                  <Campo
                    type="date"
                    name="emissao_da_ctps"
                    rotulo="Data de emissão da CTPS"
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="titulo_de_eleitor" rotulo="Título de eleitor" />
                </div>
                <div className="col-md-4">
                  <Campo name="zona" rotulo="Zona de votação" />
                </div>
                <div className="col-md-4">
                  <Campo name="secao" rotulo="Seção de votação" />
                </div>
                <div className="col-md-4">
                  <Campo name="cnh" rotulo="CNH" />
                </div>
                <div className="col-md-4">
                  <Campo name="codigo_da_cnh" rotulo="Código da CNH" />
                </div>
                <div className="col-md-4">
                  <Campo
                    type="date"
                    name="validade_da_cnh"
                    rotulo="Validade da CNH"
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="telefone1"
                    rotulo="Telefone 1"
                    mascara={MASCARA_TELEFONE}
                    prefixar={<Icone nome="telephone-fill" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="telefone2"
                    rotulo="Telefone 2"
                    mascara={MASCARA_TELEFONE}
                    prefixar={<Icone nome="telephone-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="cep"
                    rotulo="CEP"
                    prefixar={<Icone nome="geo-fill" />}
                    aoDeixar={setCep}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="estado"
                    rotulo="Estado onde reside"
                    opcoes={ESTADOS}
                    forcarValor={viacep?.uf}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="cidade" rotulo="Cidade onde reside" forcarValor={viacep?.localidade} />
                </div>
                <div className="col-md-4">
                  <Campo name="bairro" rotulo="Bairro onde reside" forcarValor={viacep?.bairro} />
                </div>
                <div className="col-md-6">
                  <Campo name="endereco" rotulo="Endereço de onde reside" forcarValor={viacep?.logradouro} />
                </div>
                <div className="col-md-2">
                  <Campo name="numero" type="number" rotulo="Número" />
                </div>
                <div className="col-md-12">
                  <Campo name="complemento" rotulo="Complemento" />
                </div>
                <div className="col-md-6">
                  <Campo name="nome_da_mae" rotulo="Nome da mãe" />
                </div>
                <div className="col-md-6">
                  <Campo name="nome_do_pai" rotulo="Nome do pai" />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="escolaridade"
                    rotulo="Escolaridade"
                    opcoes={ESCOLARIDADES}
                    prefixar={<Icone nome="mortarboard-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="deficiente"
                    rotulo="É definiciente"
                    opcoes={SIM_OU_NAO}
                    prefixar={<Icone nome="sunglasses" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="deficiencia" rotulo="Qual definiciencia" />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="estado_civil"
                    rotulo="Estado civil"
                    opcoes={ESTADOS_CIVIS}
                    prefixar={<Icone nome="heart-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="filhos"
                    rotulo="Possui filhos até 14 anos"
                    opcoes={SIM_OU_NAO}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="quantos_filhos" rotulo="Quantos filhos" />
                </div>
                <div className="col-md-12">
                  <CaixaDeTexto
                    name="observacoes"
                    rotulo="Observações"
                    semMargem
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="bg-white shadow-sm rounded p-4">
              <Arquivo name="imagem" />
              <Selecao
                name="indicado_por_id"
                rotulo="Indicação de"
                opcoes={usuarios}
              />
              <Selecao
                name="permissao"
                rotulo="Permissão"
                prefixar={<Icone nome="lock-fill" />}
                opcoes={PERMISSOES}
              />
              <Selecao
                name="permissao_os"
                rotulo="Permissão para OS"
                prefixar={<Icone nome="lock-fill" />}
                opcoes={PERMISSOES_OS}
              />

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink tema="light" to="/usuarios" className="ms-2">
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Adicionar;
